import React from "react"
import { ArrowSubmitButton, ReimbursementDialogFraming, BackArrow } from "../shared/layouts"
import { Progress } from "#/components/views/modal/Progress"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useReimbursementContext } from "#/components/context/reimbursement/GetReimbursedContext"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { JourneyTypes } from "./ExpenseJourneyTypes"
import { FormattedMessage, useIntl } from "react-intl"
import { DialogTitle, DialogContentText, Box, Link, Typography } from "@carrotfertility/carotene-core"
import { DialogActionsBar, Form, FormOnSubmitHandler, FormSelect, useWatch } from "@carrotfertility/carotene-core-x"
import { RequirementCard } from "./modules/requirement-card"
import { Link as ReactRouterLink } from "react-router-dom"
import { useFilteredAndFormattedReimbursementJourneyOptions } from "#/components/get-reimbursed/shared/reimbursementJourneys"

const isQME = (answer: string): boolean => {
  return ["FERTILITY_CARE", "PRESERVATION", "PATHFINDING"].includes(answer)
}

// Component to show warning when Metabolic-fertility health is selected
const MetabolicFertilityHealthWarning = (): JSX.Element | null => {
  const selectedExpenseType = useWatch({ name: "expenseRelatedTo" })

  // Only show warning if Metabolic-fertility health is selected
  if (selectedExpenseType !== JourneyTypes.METABOLIC_FERTILITY_HEALTH) {
    return null
  }

  return (
    <RequirementCard
      title={<FormattedMessage defaultMessage="Care requirements" />}
      body={[
        <Typography key="sprints-requirement" variant="body1">
          <FormattedMessage
            defaultMessage="This type of care is only eligible when received through the Sprints program. Learn more in your <link>benefit guide</link>."
            values={{
              link: (linkContent) => (
                <Link
                  component={ReactRouterLink}
                  target="_blank"
                  to="/your-benefit-guide/additional-at-home-care/metabolic-fertility-health"
                  color="inherit"
                >
                  {linkContent}
                </Link>
              )
            }}
          />
        </Typography>
      ]}
    />
  )
}

export const AboutThisExpenseExpenseRelatedTo = (): JSX.Element => {
  const { send, back } = useStateMachine()
  const { updateExpenseRelatedTo, updateJourneySubtype, updateWhatKindOfJourney, state } = useReimbursementContext()
  const { isUsa } = useCurrentUser()
  const intl = useIntl()
  const filteredAndFormattedReimbursementJourneyOptions = useFilteredAndFormattedReimbursementJourneyOptions()

  const updateJourneyTypeAndSubtype = (answer: string): void => {
    // if expenseRelatedTo changes clear out whatKindOfJourney/JourneySubType
    if (state.expenseRelatedTo.value !== answer) {
      updateJourneySubtype({})
      updateWhatKindOfJourney({})
    }
  }

  const handleOnSubmit: FormOnSubmitHandler<{ expenseRelatedTo: string }> = (values): void => {
    const answer = values.expenseRelatedTo
    const answerAsSelectOption = filteredAndFormattedReimbursementJourneyOptions.find((j) => j.value === answer)
    updateJourneyTypeAndSubtype(answer)
    updateExpenseRelatedTo(answerAsSelectOption)
    if (answer === "ADOPTION" && isUsa) {
      send("ADOPTION")
      return
    }
    if (
      [
        JourneyTypes.PREGNANCY_AND_POSTPARTUM_SERVICES,
        JourneyTypes.MENOPAUSE,
        JourneyTypes.LOW_TESTOSTERONE,
        JourneyTypes.GENDER_AFFIRMING_CARE,
        JourneyTypes.METABOLIC_FERTILITY_HEALTH
      ].includes(answer as JourneyTypes)
    ) {
      send("WHAT_SERVICES")
      return
    }
    if ([JourneyTypes.PRESERVATION, "GC"].includes(answer)) {
      send("STORAGE_FEES")
      return
    }
    if (!isQME(answer)) {
      send("ADDITIONAL_INFO_ANYTHING_ELSE")
      return
    }
    if (answer === JourneyTypes.FERTILITY_CARE) {
      send("FERTILITY_CARE")
      return
    }
    send("")
  }

  return (
    <Form onSubmit={handleOnSubmit} defaultValues={{ expenseRelatedTo: state?.expenseRelatedTo?.value || null }}>
      <Progress
        numSteps={3}
        step={2}
        label={intl.formatMessage({ defaultMessage: "About This Expense" })}
        inactiveStepBackgroundColor="#DBE6EE"
      />
      <BackArrow onClick={back} />
      <ReimbursementDialogFraming>
        <DialogTitle id="step-heading">
          <FormattedMessage defaultMessage="What is this expense related to?" />
        </DialogTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.spacing(theme.tokens.spacing.lg) }}>
          <DialogContentText>
            <FormattedMessage defaultMessage="If you're not sure, choose the option that seems like the best fit." />
          </DialogContentText>
          <FormSelect
            aria-labelledby="step-heading"
            name="expenseRelatedTo"
            controllerProps={{
              rules: {
                required: intl.formatMessage({ defaultMessage: "Required" })
              }
            }}
            label={intl.formatMessage({ defaultMessage: "Select option" })}
            options={filteredAndFormattedReimbursementJourneyOptions}
          />
          <MetabolicFertilityHealthWarning />
        </Box>
      </ReimbursementDialogFraming>
      <DialogActionsBar
        primaryAction={
          <ArrowSubmitButton>
            <FormattedMessage defaultMessage="Continue" />
          </ArrowSubmitButton>
        }
      />
    </Form>
  )
}
